import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import PortalAccessScreen from './PortalAccessScreen';
import PortalOtpScreen from './PortalOtpScreen';
import PortalApplicationScreen from './PortalApplicationScreen';
import PortalSuccessScreen from './PortalSuccessScreen';
import PortalMyApplicationsScreen from './PortalMyApplicationsScreen';
import PortalMyTestsScreen from './PortalMyTestsScreen';
import PortalAlreadyAppliedScreen from './PortalAlreadyAppliedScreen';
import PortalBrandingHeader from './PortalBrandingHeader';
import PortalHeader from './PortalHeader';
import PortalProfileScreen from './PortalProfileScreen';
import PortalIdentityStep from './PortalIdentityStep';
import PortalInvitationMismatch from './PortalInvitationMismatch';
import { clearFairAnswers } from './fairMemory';

const SESSION_TOKEN_KEY = 'candidate_session_token';

const SCREEN = {
  LOADING: 'loading',
  NOT_AVAILABLE: 'not_available',
  ACCESS: 'access',
  OTP: 'otp',
  APPLICATION: 'application',
  MY_APPLICATIONS: 'my_applications',
  MY_TESTS: 'my_tests',
  PROFILE: 'profile',
  SUCCESS: 'success',
  ALREADY_APPLIED: 'already_applied',
  // "Completa tus datos": nacionalidad + documento (registro o cuenta sin documento)
  IDENTITY: 'identity',
  // Link de una invitación por correo abierto con la sesión de otro correo
  INVITATION_MISMATCH: 'invitation_mismatch',
};

// La cuenta tiene documento verificado (rut + país del documento)
const hasDocument = (candidate) => Boolean(candidate?.rut && candidate?.documentCountry);

// Pantallas donde hay sesión activa y se muestra la barra de cuenta
const AUTHENTICATED_SCREENS = [
  SCREEN.APPLICATION,
  SCREEN.MY_APPLICATIONS,
  SCREEN.MY_TESTS,
  SCREEN.PROFILE,
  SCREEN.SUCCESS,
  SCREEN.ALREADY_APPLIED,
  SCREEN.IDENTITY,
];

const PortalLayout = () => {
  const { positionId } = useParams();
  const location = useLocation();
  // Vista solicitada por URL (p. ej. /portal?view=profile desde el job board)
  const requestedView = new URLSearchParams(location.search).get('view');
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Feria desde la que se llegó (?feria=<slug>, PLAN_FERIAS): la postulación
  // queda marcada con ella y las pantallas ofrecen volver a sus vacantes
  const fairParam = new URLSearchParams(location.search).get('feria');
  const fairSlug = fairParam && /^[a-z0-9-]{1,80}$/i.test(fairParam) ? fairParam.toLowerCase() : null;
  const fairHref = fairSlug ? `/feria/${fairSlug}` : null;
  // "Ingresar" desde la feria (sin vacante ni vista pedida): al terminar el
  // acceso se vuelve a la feria
  const returnsToFair = Boolean(fairHref && !positionId && !requestedView);
  // Link de una invitación por correo (?inv=<código>, 2026-10-09): el portal
  // sabe a qué correo se envió y avisa si está abierta la sesión de otro
  const invParam = new URLSearchParams(location.search).get('inv');
  const invitationCode = invParam && /^[a-f0-9]{32}$/i.test(invParam) ? invParam.toLowerCase() : null;
  // Link de una vacante sin marca (2026-10-25): /portal/apply/:id era el link
  // que entregaba el ATS; ahora lleva al detalle de la vacante (/vacante/:id),
  // donde recién al apretar Postular se pide el correo. Siguen directo al
  // formulario el botón Postular (?postular=1), las invitaciones por correo
  // (?inv=) y las ferias (?feria=).
  const goesToDetail = Boolean(positionId)
    && !new URLSearchParams(location.search).has('postular')
    && invParam === null
    && fairParam === null;

  const [screen, setScreen] = useState(SCREEN.LOADING);
  const [position, setPosition] = useState(null);
  // Datos del paso de acceso: { dni, country, email, maskedEmail }
  const [access, setAccess] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [candidateId, setCandidateId] = useState(null);
  const [applicantId, setApplicantId] = useState(null);
  const [candidateData, setCandidateData] = useState(null);
  const [existingApplication, setExistingApplication] = useState(null);
  // La postulación quedó "Por validar" (PLAN_IMPORTACION_COMPUTRABAJO, fase 0):
  // la pantalla final avisa que un reclutador revisará sus datos
  const [successPending, setSuccessPending] = useState(false);
  // Ya está en la vacante porque la agregó el reclutador y no la ha completado
  // (la postulación viene "completable"): en vez de "Ya postulaste" se abre el
  // formulario para completarla, y la pantalla final lo dice (2026-10-08)
  const [completing, setCompleting] = useState(false);
  const [successCompleted, setSuccessCompleted] = useState(false);
  // Registro pendiente: correo verificado sin cuenta { token, email } (la
  // cuenta se crea recién al completar los datos, PLAN_MASIVO_PORTAL)
  const [registration, setRegistration] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  // Invitación del link: { invitedEmail (oculto), sameAccount: true|false|null }
  const [invitation, setInvitation] = useState(null);

  // Si hay positionId, verifica si el candidato ya postuló a esa vacante.
  // Si la consulta falla, devuelve null y se sigue al formulario (no bloquea).
  const findExistingApplication = async (candId, token) => {
    if (!positionId || !candId || !token) return null;
    try {
      const response = await api.get(`/applications`, {
        params: { applicantId: candId },
        headers: { 'x-candidate-token': token },
      });
      if (response.data.response?.status_response === 'OK') {
        const apps = response.data.response.data || [];
        return apps.find((a) => a.positionId === positionId) || null;
      }
    } catch (err) {
      console.error('Error checking existing application:', err);
    }
    return null;
  };

  useEffect(() => {
    // Se redirige al detalle de la vacante (abajo): no se carga nada
    if (goesToDetail) return;
    const init = async () => {
      try {
        // 1. Cargar datos de la vacante si hay positionId
        if (positionId) {
          try {
            const posResponse = await api.get(`/positions-public/${positionId}`);
            if (posResponse.data.response?.status_response === 'OK') {
              const posData = posResponse.data.response.data;
              setPosition(posData);
              // Solo las vacantes activas aceptan postulación pública:
              // borradores y cerradas no se muestran ni permiten postular.
              // Además con "Visible en portal" encendido (portalOpen); las
              // masivas, también con cupo (PLAN_MASIVO_PORTAL). Una hunting
              // sin portalOpen (backend anterior) sigue como antes.
              const portalClosed = posData?.tipo === 'massive' ? !posData?.portalOpen : posData?.portalOpen === false;
              if (posData?.status !== 'active' || portalClosed) {
                setScreen(SCREEN.NOT_AVAILABLE);
                return;
              }
            }
          } catch (err) {
            console.error('Error loading position:', err);
            // Vacante inexistente o eliminada (404): no se puede postular
            if (err?.response?.status === 404) {
              setScreen(SCREEN.NOT_AVAILABLE);
              return;
            }
          }
        }

        // 2. Verificar sessionToken en localStorage
        const storedToken = localStorage.getItem(SESSION_TOKEN_KEY);

        // Link de una invitación por correo: a qué correo se envió (oculto) y
        // si es el de la sesión abierta. Código inexistente o backend anterior:
        // el portal sigue como siempre
        let inv = null;
        if (positionId && invitationCode) {
          try {
            const invResponse = await api.get(`/positions-public/${positionId}/invitation/${invitationCode}`, {
              headers: storedToken ? { 'x-candidate-token': storedToken } : {},
            });
            if (invResponse.data.response?.status_response === 'OK') inv = invResponse.data.response.data || null;
          } catch (err) {
            inv = null;
          }
          setInvitation(inv);
        }

        if (storedToken) {
          try {
            const meResponse = await api.get(
              `/public/candidates/me`,
              { headers: { 'x-candidate-token': storedToken } }
            );

            if (meResponse.data.response?.status_response === 'OK') {
              const candidate = meResponse.data.response.data;
              setSessionToken(storedToken);
              setCandidateId(candidate.uid);
              setCandidateData(candidate);

              // La invitación es para otro correo (p. ej. equipo compartido):
              // entrar con el de la invitación o seguir con esta cuenta
              if (inv && inv.sameAccount === false) {
                setScreen(SCREEN.INVITATION_MISMATCH);
                return;
              }

              // Cuenta sin documento: se le pide antes de seguir
              if (!hasDocument(candidate)) {
                setScreen(SCREEN.IDENTITY);
                return;
              }

              const existing = await findExistingApplication(candidate.uid, storedToken);
              if (existing && !existing.completable) {
                setExistingApplication(existing);
                setScreen(SCREEN.ALREADY_APPLIED);
                return;
              }
              setCompleting(Boolean(existing && existing.completable));

              if (returnsToFair) {
                navigate(fairHref);
                return;
              }

              setScreen(resolveScreenAfterAuth(candidate, positionId));
              return;
            }
          } catch (err) {
            localStorage.removeItem(SESSION_TOKEN_KEY);
          }
        }

        setScreen(SCREEN.ACCESS);
      } catch (err) {
        console.error(err);
        setScreen(SCREEN.ACCESS);
      }
    };

    init();
  }, [positionId, goesToDetail]);

  // Determina a qué pantalla ir según si el candidato tiene perfil completo
  const resolveScreenAfterAuth = (candidate, posId) => {
    if (posId) return SCREEN.APPLICATION;
    if (requestedView === 'profile') return SCREEN.PROFILE;
    if (requestedView === 'tests') return SCREEN.MY_TESTS;
    if (!candidate.name) return SCREEN.APPLICATION;
    return SCREEN.MY_APPLICATIONS;
  };

  const handleAccessSubmit = (accessData) => {
    setAccess(accessData);
    setScreen(SCREEN.OTP);
  };

  // Ya con sesión y documento: postulación existente o la pantalla que toca
  const continueAfterAuth = async (candId, token, candData) => {
    const existing = await findExistingApplication(candId, token);
    if (existing && !existing.completable) {
      setExistingApplication(existing);
      setScreen(SCREEN.ALREADY_APPLIED);
      return;
    }
    setCompleting(Boolean(existing && existing.completable));

    if (returnsToFair) {
      navigate(fairHref);
      return;
    }

    setScreen(resolveScreenAfterAuth(candData || {}, positionId));
  };

  const handleOtpSuccess = async (token, candId, candData, extra = {}) => {
    // Correo sin cuenta: la cuenta se crea en "Completa tus datos"
    if (extra.registrationToken) {
      setRegistration({ token: extra.registrationToken, email: access?.email || '' });
      setScreen(SCREEN.IDENTITY);
      return;
    }

    localStorage.setItem(SESSION_TOKEN_KEY, token);
    setSessionToken(token);
    setCandidateId(candId);
    setCandidateData(candData);
    setRegistration(null);

    // Cuenta sin documento: se le pide antes de seguir
    if (extra.needsIdentity || (candData && !hasDocument(candData))) {
      setScreen(SCREEN.IDENTITY);
      return;
    }

    await continueAfterAuth(candId, token, candData);
  };

  // Registro completado: la cuenta nace con correo + documento → sesión
  const handleRegistered = async (token, candId) => {
    let candData = null;
    try {
      const meResponse = await api.get(`/public/candidates/me`, { headers: { 'x-candidate-token': token } });
      if (meResponse.data.response?.status_response === 'OK') candData = meResponse.data.response.data;
    } catch (err) {
      console.error(err);
    }
    setRegistration(null);
    localStorage.setItem(SESSION_TOKEN_KEY, token);
    setSessionToken(token);
    setCandidateId(candId);
    setCandidateData(candData);
    await continueAfterAuth(candId, token, candData);
  };

  // Documento completado en una cuenta antigua
  const handleIdentityCompleted = async (updated) => {
    const data = updated || candidateData;
    if (updated) setCandidateData(updated);
    await continueAfterAuth(candidateId, sessionToken, data);
  };

  // El documento es de otra cuenta: se entra a esa cuenta con su código
  const handleUseOtherAccount = (accessData) => {
    setRegistration(null);
    setAccess(accessData);
    setScreen(SCREEN.OTP);
  };

  // Volver a ingresar el correo (registro vencido o "Volver")
  const handleRestartAccess = () => {
    setRegistration(null);
    setScreen(SCREEN.ACCESS);
  };

  // "Postular" sobre una vacante ya postulada (ALREADY_EXISTS del backend):
  // mostrar la pantalla "Ya postulaste" con los datos de la postulación original
  const handleAlreadyApplied = (application) => {
    setExistingApplication(application);
    setScreen(SCREEN.ALREADY_APPLIED);
  };

  const handleApplicationSuccess = (result) => {
    setSuccessPending(Boolean(result && result.pendingValidation));
    setSuccessCompleted(Boolean(result && result.completed));
    // Si hay positionId → pantalla de éxito
    // Si no hay positionId → era solo completar perfil → mis postulaciones
    if (positionId) {
      setScreen(SCREEN.SUCCESS);
    } else {
      setScreen(SCREEN.MY_APPLICATIONS);
    }
  };

  // Navegación de la barra de cuenta (Mis postulaciones / Mi perfil)
  const handleNavigate = (target) => {
    if (target === 'profile') setScreen(SCREEN.PROFILE);
    else if (target === 'my_applications') setScreen(SCREEN.MY_APPLICATIONS);
    else if (target === 'my_tests') setScreen(SCREEN.MY_TESTS);
  };

  // Cerrar sesión: revoca la sesión en el backend, limpia el token local y
  // vuelve a la pantalla de acceso. Si el backend falla igual se cierra local.
  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      if (sessionToken) {
        await api.post(
          `/public/candidates/logout`,
          {},
          { headers: { 'x-candidate-token': sessionToken } }
        );
      }
    } catch (err) {
      console.error('Error closing session:', err);
    } finally {
      localStorage.removeItem(SESSION_TOKEN_KEY);
      // Respuestas recordadas en ferias (PLAN_FERIAS, fase 2): no quedan para la
      // siguiente persona que use este navegador
      clearFairAnswers();
      setSessionToken(null);
      setCandidateId(null);
      setCandidateData(null);
      setExistingApplication(null);
      setCompleting(false);
      setAccess(null);
      setRegistration(null);
      setLoggingOut(false);
      setScreen(SCREEN.ACCESS);
    }
  };

  // Invitación para otro correo: "Seguir como …" sigue con la cuenta abierta
  // (lo mismo que al entrar con sesión); "Entrar con el correo de la
  // invitación" cierra la sesión y vuelve al acceso, con el aviso del correo
  const handleContinueWithSession = async () => {
    if (!hasDocument(candidateData)) {
      setScreen(SCREEN.IDENTITY);
      return;
    }
    await continueAfterAuth(candidateId, sessionToken, candidateData);
  };

  // Perfil guardado desde "Mi perfil": refrescar los datos en memoria
  const handleProfileSaved = (updated) => {
    setCandidateData(updated);
  };

  if (goesToDetail) {
    return <Navigate to={{ pathname: `/vacante/${encodeURIComponent(positionId)}`, search: location.search }} replace />;
  }

  if (screen === SCREEN.LOADING) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
      <PortalBrandingHeader branding={position?.branding} />
      {sessionToken && AUTHENTICATED_SCREENS.includes(screen) && (
        <PortalHeader
          hasSession
          current={screen}
          onNavigate={handleNavigate}
          onLogout={handleLogout}
          loggingOut={loggingOut}
          fairSlug={fairSlug}
          sessionEmail={candidateData?.email || null}
        />
      )}
      {screen === SCREEN.NOT_AVAILABLE && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 12, px: 2, textAlign: 'center' }}>
          <Box component="span" sx={{ fontSize: 22, fontWeight: 700 }}>
            Esta vacante no está disponible
          </Box>
          <Box component="span" sx={{ color: 'text.secondary' }}>
            El proceso no está recibiendo postulaciones en este momento.
          </Box>
          {fairHref && (
            <Button variant="outlined" href={fairHref} sx={{ mt: 2 }}>
              {t('portal.fair.backToFair')}
            </Button>
          )}
        </Box>
      )}
      {screen === SCREEN.ACCESS && (
        <PortalAccessScreen
          position={position}
          onSubmit={handleAccessSubmit}
          initialEmail={access?.mode === 'email' ? access.email : ''}
          invitationEmail={invitation?.invitedEmail || null}
        />
      )}
      {screen === SCREEN.INVITATION_MISMATCH && (
        <PortalInvitationMismatch
          position={position}
          invitedEmail={invitation?.invitedEmail || ''}
          sessionEmail={candidateData?.email || ''}
          onUseInvitation={handleLogout}
          onContinue={handleContinueWithSession}
          loading={loggingOut}
        />
      )}
      {screen === SCREEN.OTP && (
        <PortalOtpScreen
          access={access}
          positionId={positionId}
          onSuccess={handleOtpSuccess}
          onBack={() => setScreen(SCREEN.ACCESS)}
        />
      )}
      {screen === SCREEN.IDENTITY && (
        <PortalIdentityStep
          position={position}
          mode={registration ? 'register' : 'complete'}
          email={registration ? registration.email : (candidateData?.email || '')}
          registrationToken={registration?.token || null}
          sessionToken={sessionToken}
          onDone={registration ? handleRegistered : handleIdentityCompleted}
          onUseOtherAccount={handleUseOtherAccount}
          onRestart={handleRestartAccess}
        />
      )}
      {screen === SCREEN.APPLICATION && (
        <PortalApplicationScreen
          position={position}
          candidateId={candidateId}
          applicantId={applicantId}
          candidateData={candidateData}
          sessionToken={sessionToken}
          positionId={positionId}
          fairSlug={fairSlug}
          onSuccess={handleApplicationSuccess}
          onAlreadyApplied={handleAlreadyApplied}
          completing={completing}
        />
      )}
      {screen === SCREEN.MY_APPLICATIONS && (
        <PortalMyApplicationsScreen
          candidateId={candidateId}
          candidateData={candidateData}
          sessionToken={sessionToken}
        />
      )}
      {screen === SCREEN.MY_TESTS && (
        <PortalMyTestsScreen
          candidateData={candidateData}
          sessionToken={sessionToken}
        />
      )}
      {screen === SCREEN.PROFILE && (
        <PortalProfileScreen
          candidateData={candidateData}
          sessionToken={sessionToken}
          onSaved={handleProfileSaved}
        />
      )}
      {screen === SCREEN.SUCCESS && (
        <PortalSuccessScreen position={position} fairHref={fairHref} pendingValidation={successPending} completed={successCompleted} />
      )}
      {screen === SCREEN.ALREADY_APPLIED && (
        <PortalAlreadyAppliedScreen
          position={position}
          application={existingApplication}
          onViewApplications={() => setScreen(SCREEN.MY_APPLICATIONS)}
          fairHref={fairHref}
        />
      )}
    </Box>
  );
};

export default PortalLayout;